(() => {
  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => [...root.querySelectorAll(selector)];
  const routes = window.HavenThread?.routes || { cart: '/cart', cartAdd: '/cart/add', cartChange: '/cart/change' };
  let toastTimer;

  const escapeHTML = value => String(value ?? '').replace(/[&<>'"]/g, char => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', "'":'&#39;', '"':'&quot;' }[char]));
  const formatMoney = (cents, currency = 'GBP') => new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style:'currency', currency }).format(cents / 100);

  function toast(message) {
    const element = $('[data-toast]');
    if (!element) return;
    element.textContent = message;
    element.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => element.classList.remove('is-visible'), 2300);
  }

  function openDrawer() {
    const drawer = $('[data-cart-drawer]');
    const overlay = $('[data-overlay]');
    if (!drawer || !overlay) return;
    overlay.hidden = false;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('drawer-open');
    setTimeout(() => $('[data-close-drawer]', drawer)?.focus(), 40);
  }

  function closeDrawer() {
    const drawer = $('[data-cart-drawer]');
    const overlay = $('[data-overlay]');
    drawer?.classList.remove('is-open');
    drawer?.setAttribute('aria-hidden', 'true');
    if (overlay) overlay.hidden = true;
    document.body.classList.remove('drawer-open');
  }

  async function getCart() {
    const response = await fetch(`${routes.cart}.js`, { headers: { Accept:'application/json' } });
    if (!response.ok) throw new Error('Unable to load bag');
    return response.json();
  }

  function renderCart(cart) {
    $$('[data-cart-count]').forEach(element => { element.textContent = cart.item_count; });
    const drawerCount = $('[data-cart-drawer-count]');
    if (drawerCount) drawerCount.textContent = `(${cart.item_count})`;
    const total = $('[data-cart-total]');
    if (total) total.textContent = formatMoney(cart.total_price, cart.currency || 'GBP');
    const footer = $('[data-cart-footer]');
    if (footer) footer.hidden = cart.item_count === 0;
    const container = $('[data-cart-items]');
    if (!container) return;

    if (!cart.items.length) {
      container.innerHTML = '<div class="empty-bag"><strong>Your bag is waiting.</strong><p>Add a few considered essentials.</p></div>';
      return;
    }

    container.innerHTML = cart.items.map((item, index) => {
      const image = item.featured_image?.url || item.image || '';
      return `<article class="drawer-item" data-line-item="${index + 1}">
        <a href="${escapeHTML(item.url)}" class="drawer-item__image">${image ? `<img src="${escapeHTML(image)}&width=220" alt="${escapeHTML(item.featured_image?.alt || item.product_title)}" width="220" height="220" loading="lazy">` : ''}</a>
        <div><small>${escapeHTML(item.product_type || '')}</small><a href="${escapeHTML(item.url)}"><strong>${escapeHTML(item.product_title)}</strong></a><span>${formatMoney(item.final_price, cart.currency || 'GBP')}</span>
          <div class="quantity-control"><button type="button" data-cart-change="${index + 1}" data-quantity="${Math.max(0, item.quantity - 1)}" aria-label="Decrease quantity">−</button><span>${item.quantity}</span><button type="button" data-cart-change="${index + 1}" data-quantity="${item.quantity + 1}" aria-label="Increase quantity">+</button></div>
        </div><strong>${formatMoney(item.final_line_price, cart.currency || 'GBP')}</strong>
      </article>`;
    }).join('');
  }

  async function updateCartDrawer() {
    const cart = await getCart();
    renderCart(cart);
    return cart;
  }

  function bindCartForms(root = document) {
    $$('[data-product-form]', root).forEach(form => {
      if (form.dataset.bound === 'true') return;
      form.dataset.bound = 'true';
      form.addEventListener('submit', async event => {
        event.preventDefault();
        const button = $('button[type="submit"]', form);
        const original = button?.textContent;
        if (button) { button.disabled = true; button.textContent = 'Adding…'; }
        try {
          const response = await fetch(`${routes.cartAdd}.js`, { method:'POST', headers:{ Accept:'application/json' }, body:new FormData(form) });
          if (!response.ok) throw new Error('Unable to add this product');
          await updateCartDrawer();
          openDrawer();
          toast('Added to your bag');
        } catch (error) {
          toast(error.message || 'Please try again');
        } finally {
          if (button) { button.disabled = false; button.textContent = original; }
        }
      });
    });
  }

  function bindFavourites(root = document) {
    let saved;
    try { saved = new Set(JSON.parse(localStorage.getItem('haven-thread-favourites') || '[]')); }
    catch (_) { saved = new Set(); }
    $$('[data-favourite]', root).forEach(button => {
      if (saved.has(button.dataset.favourite)) button.classList.add('is-active');
      if (button.dataset.bound === 'true') return;
      button.dataset.bound = 'true';
      button.addEventListener('click', () => {
        const key = button.dataset.favourite;
        if (saved.has(key)) saved.delete(key); else saved.add(key);
        button.classList.toggle('is-active', saved.has(key));
        button.setAttribute('aria-label', `${saved.has(key) ? 'Remove' : 'Save'} favourite`);
        try { localStorage.setItem('haven-thread-favourites', JSON.stringify([...saved])); } catch (_) {}
        toast(saved.has(key) ? 'Saved to favourites' : 'Removed from favourites');
      });
    });
  }

  function initShop(section) {
    if (!section || section.dataset.initialized === 'true') return;
    section.dataset.initialized = 'true';
    const grid = $('[data-product-grid]', section);
    const cards = $$('[data-product-card]', section);
    const search = $('[data-product-search]', section);
    const categorySelect = $('[data-category-select]', section);
    const priceFilter = $('[data-price-filter]', section);
    const sortFilter = $('[data-sort-filter]', section);
    const summary = $('[data-results-summary]', section);
    const empty = $('[data-empty-results]', section);
    const state = { category:'home', price:'all', query:'', sort:'featured' };
    cards.forEach((card, index) => { card.dataset.originalOrder = index; });

    const apply = () => {
      const visible = cards.filter(card => {
        const category = (card.dataset.category || '').toLowerCase();
        const price = Number(card.dataset.price || 0);
        const title = ($('h3', card)?.textContent || '').toLowerCase();
        const matchesCategory = state.category === 'all' || category.includes(state.category);
        const matchesQuery = !state.query || title.includes(state.query);
        const matchesPrice = state.price === 'all' || (state.price === 'under25' && price < 25) || (state.price === '25to40' && price >= 25 && price <= 40) || (state.price === 'over40' && price > 40);
        const show = matchesCategory && matchesQuery && matchesPrice;
        card.classList.toggle('is-hidden', !show);
        return show;
      });
      if (state.sort === 'priceAsc') cards.sort((a,b) => Number(a.dataset.price) - Number(b.dataset.price));
      if (state.sort === 'priceDesc') cards.sort((a,b) => Number(b.dataset.price) - Number(a.dataset.price));
      if (state.sort === 'name') cards.sort((a,b) => ($('h3',a)?.textContent || '').localeCompare($('h3',b)?.textContent || ''));
      if (state.sort === 'featured') cards.sort((a,b) => Number(a.dataset.originalOrder) - Number(b.dataset.originalOrder));
      cards.forEach(card => grid.appendChild(card));
      if (summary) summary.textContent = `Showing ${visible.length} ${visible.length === 1 ? 'piece' : 'pieces'}${state.query ? ` for “${state.query}”` : ''}`;
      if (empty) empty.hidden = visible.length !== 0;
      $$('[data-category-filter]', section).forEach(button => button.classList.toggle('is-active', button.dataset.categoryFilter === state.category));
    };

    $$('[data-category-filter]', section).forEach(button => button.addEventListener('click', () => {
      state.category = button.dataset.categoryFilter;
      if (categorySelect) categorySelect.value = state.category;
      apply();
    }));
    search?.addEventListener('input', () => { state.query = search.value.trim().toLowerCase(); apply(); });
    categorySelect?.addEventListener('change', () => { state.category = categorySelect.value; apply(); });
    priceFilter?.addEventListener('change', () => { state.price = priceFilter.value; apply(); });
    sortFilter?.addEventListener('change', () => { state.sort = sortFilter.value; apply(); });
    $('[data-clear-filters]', section)?.addEventListener('click', () => {
      state.category = 'all'; state.price = 'all'; state.query = ''; state.sort = 'featured';
      if (search) search.value = '';
      if (categorySelect) categorySelect.value = 'all';
      if (priceFilter) priceFilter.value = 'all';
      if (sortFilter) sortFilter.value = 'featured';
      apply();
    });
    bindFavourites(section);
    bindCartForms(section);
    apply();
  }

  function initGlobal() {
    $$('[data-open-cart]').forEach(button => button.addEventListener('click', async () => { try { await updateCartDrawer(); } catch (_) {} openDrawer(); }));
    $('[data-close-drawer]')?.addEventListener('click', closeDrawer);
    $('[data-overlay]')?.addEventListener('click', closeDrawer);
    $('[data-cart-items]')?.addEventListener('click', async event => {
      const button = event.target.closest('[data-cart-change]');
      if (!button) return;
      button.disabled = true;
      try {
        const response = await fetch(`${routes.cartChange}.js`, { method:'POST', headers:{ 'Content-Type':'application/json', Accept:'application/json' }, body:JSON.stringify({ line:Number(button.dataset.cartChange), quantity:Number(button.dataset.quantity) }) });
        if (!response.ok) throw new Error('Unable to update bag');
        renderCart(await response.json());
      } catch (error) { toast(error.message || 'Please try again'); }
    });
    $('[data-menu-toggle]')?.addEventListener('click', event => {
      const menu = $('#MobileMenu');
      const expanded = event.currentTarget.getAttribute('aria-expanded') === 'true';
      event.currentTarget.setAttribute('aria-expanded', String(!expanded));
      if (menu) menu.hidden = expanded;
    });
    document.addEventListener('keydown', event => { if (event.key === 'Escape') closeDrawer(); });
    $$('[data-shop-section]').forEach(initShop);
    bindCartForms();
    bindFavourites();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initGlobal);
  else initGlobal();
  document.addEventListener('shopify:section:load', event => {
    const shopSection = $('[data-shop-section]', event.target);
    if (shopSection) initShop(shopSection);
    bindCartForms(event.target);
    bindFavourites(event.target);
  });
})();
